import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col, Radio, Input, Icon, Button, Spin, Avatar, Skeleton, Popover, Tooltip, Modal, Select, Tag } from "antd";
import { getOrderTypeIcon } from "../orders/OrderTypeIcons";
import PropTypes from "prop-types";
import "./DriverPayConfiguration.scss";
import { EditPennIcon } from "../../common/CustomIcon";
import { fetchDriverPayConfigurations, saveDriverPayConfiguration, deleteDriverPayConfigurations } from "../../api/OrdersApi";
import { alertMessage } from "../../common/Common";
import manlogo from '../../../assets/images/driver.png';
import I18n from "../../common/I18n";

const TruncatedDescription = ({ description, className = "" }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        const truncated = element.scrollHeight > element.clientHeight + 1;
        setIsTruncated(truncated);
      }
    };

    const timeoutId = setTimeout(checkTruncation, 0);
    
    window.addEventListener('resize', checkTruncation);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [description]);

  if (!description) return null;

  const descriptionElement = (
    <span ref={textRef} className={className}>
      {description}
    </span>
  );

  return isTruncated ? (
    <Popover
      content={description}
      title="Description"
      placement="top"
      trigger="hover"
      overlayStyle={{ maxWidth: 250, wordWrap: 'break-word' }}
    >
      {descriptionElement}
    </Popover>
  ) : (
    descriptionElement
  );
};

const DriverPayConfiguration = ({ orderResponse,  activeKey, readOnly = false, configs: externalConfigs = null, isSegregated = false, onConfigsLoaded = null,  orderIdentifier = null, callFrom = "ORDERS",onConfigSaved = null, isLocked = false}) => {
  const orderId = orderResponse?.id;
  const orgId = orderResponse?.customer_order_details?.organization_id;
  const isInvoiceLocked = callFrom === "ORDERS" ? _.get(orderResponse, "customer_order_details.is_locked") === "true" : isLocked

  const RATE_KEYS = [
    "transportation",
    "fuel",
    "accessorial",
    "adjustment",
    "manual_adjustment",
  ];

  const mapLocationTypeToLegType = (locationType) => {
    const upperType = locationType?.toUpperCase();
    if (upperType === "PICKUP") return "PICKUP";
    if (upperType === "DELIVERY") return "DELIVERY";
    if (upperType === "LINEHAUL") return "LINEHAUL";
    return upperType;
  };

  const mapRateTypeToKey = (rateType) => {
    const upperType = rateType?.toUpperCase();
    if (upperType === "TRANSPORTATION") return "transportation";
    if (upperType === "FUEL SURCHARGE") return "fuel";
    if (upperType === "ACCESSORIAL") return "accessorial";
    if (upperType === "LOS ADJUSTMENT") return "adjustment";
    if (upperType === "MANUAL ADJUSTMENT") return "manual_adjustment";

    return rateType?.toLowerCase();
  };

  const mapChargeType = (chargeType) => {
    return chargeType?.toUpperCase() === "FLAT" ? "flat" : "percent";
  };

  const transformSingleConfigToLeg = (config, currentLegs = {}, preserveEditState = false) => {
      const legType = mapLocationTypeToLegType(config.location_type);
      if (!legType) return null;

      const locationId = config.location_id;
      if (!locationId) return null;

      const legKey = String(locationId);

      const driverName = config.driver_name || "Not Available";
      const driverCode = config.driver_code || null;

    const rates = {};
    RATE_KEYS.forEach((key) => {
      rates[key] = { type: "percent", value: null, description: "", applyOn: "" };
    });

      let segregateDriverCharges = false;
      if (config.driver_rate_params && Array.isArray(config.driver_rate_params)) {
        config.driver_rate_params.forEach((param) => {
          const rateKey = mapRateTypeToKey(param.rate_type);
          if (rateKey && rates[rateKey]) {
            const chargeValue = param.charge_value;
            const value = (chargeValue !== null && chargeValue !== undefined && chargeValue !== '')
              ? chargeValue
              : null;

            rates[rateKey] = {
              type: mapChargeType(param.charge_type),
              value,
              description: param.description || "",
              applyOn: param.settlement_per || "",
            };

            if (rateKey === "transportation" && param.segregate_driver_charges === true) {
              segregateDriverCharges = true;
            }
          }
        });
      }

      const applyMode = config.apply_mode?.toUpperCase();
      const applyRate = applyMode === "INDIVIDUAL" ? "individual" : "all";
      const isSegregatedFromApi = config.is_segregated === true || config.is_segregated === "true" || config.is_segregated === 1;

      return {
        legKey,
        legType,
        stopIndex: config.stop_index ?? null,
        collapsed: preserveEditState ? (currentLegs[legKey]?.collapsed ?? false) : false,
        isEditing: preserveEditState ? (currentLegs[legKey]?.isEditing ?? false) : false,
        applyRate,
        rates,
        driver: {
          name: driverName,
          code: driverCode,
        },
        locationId,
        isSegregated: isSegregatedFromApi,
        segregateDriverCharges,
      };
    };

    const transformApiResponseToLegs = (configs) => {
      const legs = {};
      const orderedKeys = [];

      configs.forEach((config) => {
        const legData = transformSingleConfigToLeg(config, {});
        if (legData) {
          legs[legData.legKey] = legData;
          orderedKeys.push(legData.legKey);
        }
    });

    return { legs, orderedKeys };
  };

  const transformActualRatesToCharges = (configs) => {
    const actualCharges = {};

    if (!configs || !Array.isArray(configs)) return actualCharges;

    configs.forEach((config) => {
      if (!config.actual_driver_rates || !Array.isArray(config.actual_driver_rates)) return;

      const locationId = config.location_id;
      if (!locationId) return;

      const legKey = String(locationId);
      const charges = {};

      config.actual_driver_rates.forEach((param) => {
        const rateKey = mapRateTypeToKey(param.rate_type);

        if (["transportation", "fuel", "accessorial", "adjustment", "manual_adjustment"].includes(rateKey)) {
          const chargeValue = param.charge_value;
          const value = (
            chargeValue !== null &&
              chargeValue !== undefined &&
              chargeValue !== '')
              ? chargeValue
              : null;

          if (value !== null) {
            charges[rateKey] = {
              type: mapChargeType(param.charge_type),
        value,
            };
          }
        }
      });

      if (charges.transportation !== undefined || charges.fuel !== undefined) {
        actualCharges[legKey] = charges;
      }
    });

    return actualCharges;
  };

  const [legs, setLegs] = useState({});
  const [orderedKeys, setOrderedKeys] = useState([]);
  const [actualCharges, setActualCharges] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingLegs, setSavingLegs] = useState({});
  const [isEditingAll, setIsEditingAll] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [originalLegs, setOriginalLegs] = useState(null);
  const [globalAction, setGlobalAction] = useState(null);
  const hasInitiallyFetched = useRef(false);
  const lastActiveKey = useRef(null);
  const lastOrderId = useRef(null);
  const lastOrgId = useRef(null);
  const fetchTriggered = useRef(false);
  const { confirm } = Modal;


  const fetchConfigurations = useCallback(async () => {
    if (!orderId || !orgId) return;
    
    setLoading(true);
    try {
      const result = await fetchDriverPayConfigurations(orderId, orgId);
      if (result.success) {
        if (result.configs) {
          const { legs: transformedLegs, orderedKeys: keys } = transformApiResponseToLegs(result.configs);
          setLegs(transformedLegs);
          setOrderedKeys(keys);
        } else {
          setLegs({});
          setOrderedKeys([]);
        }

        if (result.configs && Array.isArray(result.configs)) {
          setActualCharges(transformActualRatesToCharges(result.configs));
        } else {
          setActualCharges({});
        }

      if (onConfigsLoaded && result.configs && Array.isArray(result.configs)) {
      onConfigsLoaded(result.configs, orderIdentifier);
        }
      } else {
        alertMessage(result?.errors.join(','), 'error');
        setLegs({});
        setOrderedKeys([]);
        setActualCharges({});
      }
    } catch (error) {
      console.error("Error fetching driver pay configurations:", error);
      alertMessage("Failed to load driver pay configurations", "error");
    } finally {
      setLoading(false);
    }
}, [orderId, orgId, onConfigsLoaded, orderIdentifier]);
  
  useEffect(() => {
    if (readOnly && externalConfigs && Array.isArray(externalConfigs) && externalConfigs.length > 0) {
      const { legs: transformedLegs, orderedKeys: keys } = transformApiResponseToLegs(externalConfigs);
      setLegs(transformedLegs);
      setOrderedKeys(keys);
      setActualCharges({});
      return;
    }
    
    if (!readOnly && activeKey === "driverPayConfig" && orderId && orgId) {
      const currentOrderKey = `${orderId}_${orgId}`;
      const lastOrderKey = lastOrderId.current && lastOrgId.current ? `${lastOrderId.current}_${lastOrgId.current}` : null;
      
      const isDifferentOrder = lastOrderKey !== null && lastOrderKey !== currentOrderKey;

      const activeKeyChangedFromOther = lastActiveKey.current !== null &&
        lastActiveKey.current !== activeKey &&
        lastActiveKey.current !== "driverPayConfig";

      const shouldFetch =
        (!hasInitiallyFetched.current && !fetchTriggered.current) ||
        activeKeyChangedFromOther ||
        (hasInitiallyFetched.current && isDifferentOrder);

      if (shouldFetch) {
        fetchTriggered.current = true;
        hasInitiallyFetched.current = true;
        lastActiveKey.current = activeKey;
        lastOrderId.current = orderId;
        lastOrgId.current = orgId;
        fetchConfigurations();
      }
    } else if (activeKey !== "driverPayConfig") {
      if (lastActiveKey.current !== activeKey) {
        lastActiveKey.current = activeKey;
        fetchTriggered.current = false;
      }
    }
  }, [activeKey, orderId, orgId, readOnly, externalConfigs]);

  const getLegDisplayLabel = (legKey) => {
    const leg = legs[legKey];
    if (!leg) return legKey;

    const legType = leg.legType;
    const baseLabel = getLegLabel(legType);

    const legTypes = orderedKeys.map((k) => legs[k]?.legType);

    const hasRepeatingType = legTypes.some(
      (type, idx) => legTypes.indexOf(type) !== idx
    );

    if (!hasRepeatingType) {
      return baseLabel;
    }

    const sameTypeLegKeys = orderedKeys.filter(
      (k) => legs[k]?.legType === legType
    );

    const positionIndex = leg.stopIndex !== null && leg.stopIndex !== undefined
      ? leg.stopIndex
      : sameTypeLegKeys.indexOf(legKey) + 1;

    return `${baseLabel} ${positionIndex}`;
  };

  const getLegLabel = (legType) => {
    switch (legType) {
      case "PICKUP": return "Pickup";
      case "LINEHAUL": return "Linehaul";
      case "DELIVERY": return "Delivery";
      default: return legType;
    }
  };

  const getLegKeyCode = (legType) => {
    switch (legType) {
      case "PICKUP": return "R";
      case "LINEHAUL": return "LH";
      case "DELIVERY": return "D";
      default: return "";
    }
  };

  const formatRateLabel = (key) => {
    switch (key) {
      case "transportation": return "TRANSPORTATION";
      case "fuel": return "FUEL";
      case "accessorial": return "ACCESSORIAL";
      case "adjustment": return `${I18n.t('general.short_los')} ADJUSTMENT`;
      case "manual_adjustment": return "MANUAL ADJUSTMENT";
      default: return key.toUpperCase();
    }
  };

  const formatRateTypeForApi = (key) => {
    switch (key) {
      case "transportation": return "Transportation";
      case "fuel": return "Fuel Surcharge";
      case "accessorial": return "Accessorial";
      case "adjustment": return "Adjustment";
      case "manual_adjustment": return "Manual Adjustment";
      default: return key;
    }
  };

  const confirmDriverPayReset = () => {
    confirm({
      width: 500,
      title: "Reset Driver Pay Configuration?",
      content: (
        <Row>
          <Col>
            <h4>This will remove all driver pay rates configured for this order.</h4>
          </Col>
          <Col>
            <h4>Driver rates will revert to defaults.</h4>
          </Col>
        </Row>
      ),
      okText: "Yes, Reset",
      cancelText: "No",
      onOk: () => { handleGlobalReset(); },
    });
  };

  const handleGlobalEdit = () => {
    setOriginalLegs(JSON.parse(JSON.stringify(legs)));
    setIsEditingAll(true);
  };

  const handleGlobalReset = async () => {
    if (!orderId || !orgId) return;

    setGlobalAction("reset");
    setSavingAll(true);

    try {
      const payload = {
        organization_id: orgId,
        order_id: orderId,
        call_from: callFrom,
      };

      const result = await deleteDriverPayConfigurations(payload);

      if (result.success) {
        alertMessage(result?.message, "success");
        await fetchConfigurations();

        setIsEditingAll(false);
    } else {
        alertMessage(result?.errors?.join(",") || "Failed to reset configurations", "error");
      }
    } catch (e) {
      console.error("RESET ERROR:", e);
      alertMessage("Failed to reset configurations", "error");
    } finally {
      setSavingAll(false);
      setGlobalAction(null);
    }
  };


  const handleGlobalCancel = () => {
    if (originalLegs) {
      setLegs(originalLegs);
    }
    setIsEditingAll(false);
  };

  const handleGlobalSave = async () => {
    if (!orderId || !orgId) return;

    setGlobalAction("save");
    setSavingAll(true);

    try {
      const getChargeValue = (value, rateLabel = "", rateType = "percent", applyRate = "individual") => {
      if (value === null || value === undefined || value === "") return null;
  const num = parseFloat(value);
  if (isNaN(num)) return null;

        // Negative validation
  if (num < 0) {
          const labelPart = applyRate === "all" ? "" : ` for ${rateLabel}`;
          alertMessage(`Charge value${labelPart} cannot be negative`, "error");
          throw new Error("Invalid charge value");
        }

        // Percent > 100 validation
        if (rateType === "percent" && num > 100) {
          const labelPart = applyRate === "all" ? "" : ` for ${rateLabel}`;
          alertMessage(`Percentage${labelPart} cannot exceed 100`, "error");
    throw new Error("Invalid charge value");
  }

  return num;
    };

      const driver_rate_configurations = Object.values(legs).map((leg) => {
        const driver_rate_params = [];

      if (leg.applyRate === "all") {
        const rate = leg.rates.transportation;
        const chargeValue = getChargeValue(
          rate.value,
          formatRateLabel("transportation"),
          rate.type,
          "all"
        );
        const chargeType =
          rate.type.toUpperCase() === "FLAT" ? "FLAT" : "PERCENTAGE";

        RATE_KEYS.forEach((key) => {
          driver_rate_params.push({
            rate_type: formatRateTypeForApi(key),
            charge_type: chargeType,
            charge_value: chargeValue,
            description: rate.description || "",
            settlement_per:rate.applyOn || "SEGMENT",
          });
        });
      } else {
        for (const key of RATE_KEYS) {
          const r = leg.rates[key];
          const chargeValue = getChargeValue(
            r.value,
            formatRateLabel(key),
            r.type,
            "individual"
          );
          driver_rate_params.push({
            rate_type: formatRateTypeForApi(key),
            charge_type: r.type.toUpperCase() === "FLAT" ? "FLAT" : "PERCENTAGE",
            charge_value: chargeValue,
            description: r.description || "",
            settlement_per:r.applyOn || "SECTION",
          });
        }
      }

      return {
        location_id: leg.locationId,
        apply_mode: leg.applyRate === "all" ? "ALL_SECTIONS" : "INDIVIDUAL",
        driver_rate_params,
      };
    });

      const payload = {
        organization_id: orgId,
        order_id: orderId,
      call_from: callFrom,
      org_id: orgId,
      driver_rate_configurations,
    };

      const result = await saveDriverPayConfiguration(payload);

      if (result.success) {
        alertMessage("Driver pay configurations saved", "success");
        setIsEditingAll(false);
        await fetchConfigurations();
        if (onConfigSaved) onConfigSaved();
      } else {
        alertMessage(result?.errors?.join(","), "error");
      }
    } catch (e) {
      if (e?.message !== "Invalid charge value") {
        alertMessage("Failed to save configurations", "error");
      }
    } finally {
      setSavingAll(false);
      setGlobalAction(null);
    }
  };


  const formatActualCharge = (legKey, chargeType) => {
    const charges = actualCharges[legKey];
    if (!charges || !charges[chargeType] || charges[chargeType].value === null || charges[chargeType].value === undefined) {
      return null;
    }

    const charge = charges[chargeType];
    const value = Number(charge.value);

    return charge.type === "flat"
      ? `$${value.toFixed(2)}`
      : `${value.toFixed(2)}%`;
  };


const handleApplyRateChange = (legKey, value) => {
    setLegs((prev) => {
      const leg = prev[legKey];
      const newLeg = { ...leg, applyRate: value };

      if (leg.originalValues) {
        newLeg.originalValues = leg.originalValues;
      }

      if (value === "all" && leg.applyRate === "individual") {
        const transportationRate = newLeg.rates.transportation;
        const newRates = {};
        RATE_KEYS.forEach((k) => {
          newRates[k] = { ...transportationRate, applyOn: "SEGMENT" };
        });
        newLeg.rates = newRates;
      } else if (value === "individual" && leg.applyRate === "all") {
        if (leg.originalValues && leg.originalValues.applyRate === "individual") {
          const restoredRates = JSON.parse(JSON.stringify(leg.originalValues.rates));
          RATE_KEYS.forEach((k) => {
            if (restoredRates[k]?.applyOn === "SEGMENT") {
              restoredRates[k].applyOn = "SECTION";
            }
          });
          newLeg.rates = restoredRates;
        } else {
          const transportationRate = newLeg.rates.transportation;
          const newRates = {};
          RATE_KEYS.forEach((k) => {
            newRates[k] = { ...transportationRate, applyOn: "SECTION" };
          });
          newLeg.rates = newRates;
        }
      }

      return { ...prev, [legKey]: newLeg };
    });
  };

  const handleRateTypeChange = (legKey, rateType, type) => {
    setLegs((prev) => {
      const leg = prev[legKey];
      const newLeg = {
        ...leg,
        rates: {
          ...leg.rates,
          [rateType]: { ...leg.rates[rateType], type },
        },
      };

      if (leg.applyRate === "all") {
        const transportationRate = newLeg.rates.transportation;
        const newRates = {};
        RATE_KEYS.forEach((k) => {
          newRates[k] = { ...transportationRate };
        });
        newLeg.rates = newRates;
      }

      return { ...prev, [legKey]: newLeg };
    });
  };


    const handleApplyOnChange = (legKey, rateType, applyOn) => {
    setLegs((prev) => {
      const leg = prev[legKey];
      const newLeg = {
        ...leg,
        rates: {
          ...leg.rates,
          [rateType]: { ...leg.rates[rateType], applyOn },
        },
      };

      if (leg.applyRate === "all") {
        const transportationRate = newLeg.rates.transportation;
        const newRates = {};
        RATE_KEYS.forEach((k) => {
          newRates[k] = { ...transportationRate };
        });
        newLeg.rates = newRates;
      }

      return { ...prev, [legKey]: newLeg };
    });
  };

  const handleRateValueChange = (legKey, rateType, value) => {
    setLegs((prev) => {
      const leg = prev[legKey];

      let processedValue = null;

      if (value !== null && value !== undefined) {
        if (value === '') {
          processedValue = null;
        } else {
          const numValue = Number(value);
          processedValue = isNaN(numValue) ? null : numValue;
        }
}

      const newLeg = {
        ...leg,
        rates: {
          ...leg.rates,
          [rateType]: { ...leg.rates[rateType], value: processedValue },
        },
      };

      if (leg.applyRate === "all") {
        const transportationRate = newLeg.rates.transportation;
        const newRates = {};
        RATE_KEYS.forEach((k) => {
          newRates[k] = { ...transportationRate };
        });
        newLeg.rates = newRates;
      }

      return { ...prev, [legKey]: newLeg };
    });
  };

  const handleDescriptionChange = (legKey, rateType, description) => {
    setLegs((prev) => {
      const leg = prev[legKey];
      const newLeg = {
        ...leg,
        rates: {
          ...leg.rates,
          [rateType]: { ...leg.rates[rateType], description },
        },
      };

      if (leg.applyRate === "all") {
        const transportationRate = newLeg.rates.transportation;
        const newRates = {};
        RATE_KEYS.forEach((k) => {
          newRates[k] = { ...transportationRate };
        });
        newLeg.rates = newRates;
      }

      return { ...prev, [legKey]: newLeg };
    });
  };

  const getNoChargeLabel = (rateType, applyRate) => {
    if (applyRate === "all") return "No Charge";
    switch (rateType) {
      case "transportation": return "No Transportation Charge";
      case "fuel": return "No Fuel Charge";
      case "accessorial": return "No Accessorial Charge";
      case "adjustment": return `No ${I18n.t('general.short_los')} Adjustment`;
      case "manual_adjustment": return "No Manual Adjustment";
      default: return "Not Available";
    }
  };

  const getSettlementLabel = (applyOn) => {
  switch (applyOn) {
    case "LINE_ITEM": return "Per Line Item";
    case "SEGMENT":   return "Per Segment";
    case "SECTION":   return "Per Section";
    default:          return null;
  }
};


  const renderRateDisplay = (legKey, rateType) => {
    const leg = legs[legKey];
    if (!leg) return null;

    const rate = leg.rates?.[rateType];

    const isTransportationDisabled = (isSegregated || leg.isSegregated || leg.segregateDriverCharges) && rateType === "transportation";

    if (isEditingAll && !readOnly) {
      return (
        <div className="rate-edit-container">
          <div className="rate-type-toggle">
            <Button
              className={`rate-type-btn ${rate.type === "percent" ? "active" : ""}`}
              onClick={() => handleRateTypeChange(legKey, rateType, "percent")}
              disabled={isTransportationDisabled || isInvoiceLocked}
            >
              %
            </Button>
            <Button
              className={`rate-type-btn ${rate.type === "flat" ? "active" : ""}`}
              onClick={() => handleRateTypeChange(legKey, rateType, "flat")}
              disabled={isTransportationDisabled || isInvoiceLocked}
            >
              $
            </Button>
            <Input
              type="number"
              value={rate.value !== null && rate.value !== undefined ? rate.value : ''}
              onChange={(e) => handleRateValueChange(legKey, rateType, e.target.value)}
              className="rate-input"
              placeholder=""
              disabled={isTransportationDisabled || isInvoiceLocked}
              style={{width:"30%"}}
            />
          </div>
        <div className="rate-input-group">
            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <span style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2, fontWeight: 500 }}>
                Apply Settlement
              </span>
              <Select
                value={rate.applyOn || (leg.applyRate === "all" ? "SEGMENT" : "SECTION")}
                onChange={value => handleApplyOnChange(legKey, rateType, value)}
                disabled={isTransportationDisabled || isInvoiceLocked}
                style={{ width: "100%" }}
              >
                {leg.applyRate === "all" && (
                  <Select.Option value="SEGMENT">Per Segment</Select.Option>
                )}
                <Select.Option value="LINE_ITEM">Per Line Item</Select.Option>
                <Select.Option value="SECTION">Per Section</Select.Option>
              </Select>
            </div>
          </div>
            <div className="rate-input-group">
               <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <span style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2, fontWeight: 500 }}>
                Description
              </span>
            <Input
              value={rate.description}
              onChange={(e) => handleDescriptionChange(legKey, rateType, e.target.value)}
              className="description-input"
              placeholder="Description"
              // maxLength={leg.applyRate === "all" ? 50 : undefined}
              style={leg.applyRate === "all" ? { maxWidth: "200px" } : undefined}
              disabled={isTransportationDisabled || isInvoiceLocked}
            />
            </div>
          </div>
        </div>
      );
    }

      if (rate.value === null || rate.value === undefined) {
        return (
    <div className="rate-display no-charge">
      <span className="no-charge-text">
        {getNoChargeLabel(rateType, leg.applyRate)}
      </span>

{rate.description && (
  <span className="rate-description-wrapper">
    <TruncatedDescription 
      description={rate.description} 
      className="rate-description-truncated"
    />
  </span>
)}
    </div>
  );
}

      const displayValue =
        rate.type === "flat"
          ? `$${Number(rate.value).toFixed(2)}`
          : `${Number(rate.value).toFixed(2)}%`;
      return (
        <div className="rate-display">
          <span className="rate-chip">{displayValue}</span>
          {rate.applyOn && getSettlementLabel(rate.applyOn) && (
            <Tag
              color="#586FCC"
              style={{ borderRadius: 10, fontSize: 10, padding: "0 3px" }}
            >
              {getSettlementLabel(rate.applyOn)}
            </Tag>
          )}
          {rate.description && (
            <TruncatedDescription
              description={rate.description}
              className="rate-description-truncated"
            />
          )}
        </div>
      );
  };

  const renderLegCard = (legKey) => {
    const leg = legs[legKey];
    if (!leg) return null;

    const legLabel = getLegDisplayLabel(legKey);
    const keyCode = getLegKeyCode(leg.legType);

    const isSaving = savingLegs[legKey] || false;

    return (
    <Spin key={legKey} spinning={isSaving} delay={100}>
      <div className="leg-card">
        <div className="leg-card-header">
          <div className="leg-header-left">
            <div>
              {getOrderTypeIcon(keyCode, { height: "24", width: "24" })}
            </div>
            <span className="leg-label">{legLabel}</span>
            {!readOnly && (
              <>
            <span className="leg-separator">•</span>
            <div><Avatar src={manlogo} alt="DriverProfile" size={25} /></div>
            <span className="driver-info">
              {leg.driver.name}
  {leg.driver.code && ` (${leg.driver.code})`}
            </span>
            </>
            )}
            </div>
        </div>

          <div className="leg-card-body">
            <div className="apply-rate-section">
              <span className="apply-rate-label">Apply rate:</span>
              <Radio.Group
                value={leg.applyRate}
                onChange={(e) => handleApplyRateChange(legKey, e.target.value)}
                className="apply-rate-toggle"
                  disabled={readOnly || !isEditingAll}
              >
                <Radio.Button value="all">All Sections</Radio.Button>
                <Radio.Button value="individual">Individual</Radio.Button>
              </Radio.Group>
            </div>

            <div className="rates-grid">
              {leg.applyRate === "all" ? (
                <div className="rate-column rate-column-full">
                  <div className="rate-label">Rate for All Sections</div>
                  {renderRateDisplay(legKey, "transportation")}
                </div>
                ) : (
                    RATE_KEYS.map((key) => (
                      <div className="rate-column" key={key}>
                        <div className="rate-label">{formatRateLabel(key)}</div>
                        {renderRateDisplay(legKey, key)}
                      </div>
                    ))
                )
                }
            </div>

{actualCharges[legKey] && (() => {
  const actualItems = [
    { key: "transportation", label: "Transportation" },
    { key: "fuel", label: "Fuel" },
    { key: "accessorial", label: "Accessorial" },
    { key: "adjustment", label: `${I18n.t('general.short_los')} Adjustment` },
    { key: "manual_adjustment", label: "Manual Adjustment" },
  ];

  const visibleActuals = actualItems
    .map((item) => ({
      label: item.label,
      value: formatActualCharge(legKey, item.key)
    }))
    .filter((i) => i.value);

  if (visibleActuals.length === 0) return null;

  return (
    <div className="actual-charges-inline">
      <span className="actual-label">Actual Charges:</span>

      {visibleActuals.map((item, idx) => (
        <React.Fragment key={item.label}>
          <span className="actual-item">
            {item.label} : <b>{item.value}</b>
          </span>

          {idx < visibleActuals.length - 1 && (
            <span className="actual-separator">•</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
})()}
          </div>
      </div>
    </Spin>
    );
  };

  if (loading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  return (
    <Spin spinning={savingAll} tip={globalAction === "reset"
      ? "Resetting driver pay configuration..."
      : "Saving driver pay configuration..."}>
    <div className="driver-pay-configuration">
      {Object.keys(legs).length > 0 && (
          <div className="config-header-row">
            <div className="config-description">
              {!readOnly && "Configure driver rates per leg — percentage or flat amount. Rates set here override driver defaults for this order."}
            </div>

            {!readOnly && (<div className="config-actions">
              {!isEditingAll ? (
                <>
                <Button
                  type="link"
                  onClick={handleGlobalEdit}
                  className="ButtonStyle"
                >
                  <EditPennIcon width="16px" height="16px" style={{ marginRight: 4 }} />
                  Edit
                </Button>
                  <Button
                    type="link"
                    onClick={confirmDriverPayReset}
                    className="ButtonStyle"
                  >
                    <Icon type="redo" style={{ marginRight: 4 }} />
                    Reset
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="link"
                    onClick={handleGlobalSave}
                    className="ButtonStyleSave"
                    loading={savingAll}
                  >
                    <Icon type="save" style={{ marginRight: 4 }} />
                    Save
                  </Button>

                  <Button
                    type="link"
                    onClick={handleGlobalCancel}
                    className="ButtonStyle"
                  >
                    <Icon type="close" style={{ marginRight: 4 }} />
                    Cancel
                  </Button>
                </>
              )}
            
          </div>
        )}
        </div>
      )}

      <div className="legs-container">
        {orderedKeys.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            No driver pay configurations found for this order.
          </div>
        ) : (
          orderedKeys.map((legKey) => renderLegCard(legKey))
        )}
      </div>
    </div>
    </Spin>
  );
};

DriverPayConfiguration.propTypes = {
  orderResponse: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    locations: PropTypes.array,
    customer_order_details: PropTypes.shape({
      type_of_order: PropTypes.string,
      is_lh_order: PropTypes.bool,
      organization_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  account: PropTypes.shape({}),
  refreshDetails: PropTypes.func,
  activeKey: PropTypes.string,
  readOnly: PropTypes.bool,
  configs: PropTypes.array,
  isSegregated: PropTypes.bool,
  onConfigsLoaded: PropTypes.func,
  orderIdentifier: PropTypes.string,
  callFrom: PropTypes.string,
  onConfigSaved: PropTypes.func,
};

DriverPayConfiguration.defaultProps = {
  orderResponse: {},
  account: {},
  refreshDetails: () => {},
  activeKey: "",
  readOnly: false,
  configs: null,
  isSegregated: false,
  onConfigsLoaded: null,
  orderIdentifier: null,
  callFrom: "ORDERS",
  onConfigSaved: null,
};
export default DriverPayConfiguration;
